import { SummaryData } from "@backend-types";
import { DbUser, TeacherClassDetailsCourse } from "../../interfaces";
import axios from "../../services/axios";

export type StudentMap = Map<string, DbUser>;

export const addClassesToMap = (
  studentMap: StudentMap,
  newStudents: DbUser[]
): StudentMap => {
  const newMap = new Map(studentMap);
  newStudents.forEach((newStudent) => {
    newMap.set(newStudent.id, newStudent);
  });
  return newMap;
};

export const getActiveStudents = (studentMap: StudentMap): DbUser[] => {
  const activeStudents: DbUser[] = [];

  studentMap.forEach((student) => {
    if (
      !student.class_student ||
      student.class_student.length < 1 ||
      !student.class_student[0].class?.end_date ||
      new Date(student.class_student[0].class?.end_date).getTime() >= new Date().getTime()
    ) {
      activeStudents.push(student);
    }
  });

  return activeStudents;
};

export const getArchivedStudents = (studentMap: StudentMap): DbUser[] => {
  const archivedStudents: DbUser[] = [];

  studentMap.forEach((student) => {
    if (
      student.class_student &&
      student.class_student.length > 0 &&
      student.class_student[0].class?.end_date &&
      new Date(student.class_student[0].class?.end_date).getTime() < new Date().getTime()
    ) {
      archivedStudents.push(student);
    }
  });

  return archivedStudents;
};

export const getStudentsBySchoolTeacherCourse = (
  studentMap: StudentMap,
  school_id: string | null,
  teacher_id?: string | null,
  course_ids?: number[],
  archived?: boolean
): DbUser[] => {
  const filteredStudents: DbUser[] = [];

  studentMap.forEach((student) => {
    if (
      student.class_student &&
      student.class_student.length > 0 &&
      student.class_student[0].class
    ) {
      const studentSchoolId =
        student.user_school && student.user_school?.length && student.user_school[0]
          ? student.user_school[0].school_id
          : null;
      const studentTeacherId = student.class_student[0].class.teacher_id;
      const studentCourseId = student.class_student[0].class.course_id;

      const endDateExists = !!student.class_student[0].class.end_date;
      const endDateInPast =
        endDateExists &&
        new Date(student.class_student[0].class.end_date!).getTime() <
          new Date().getTime();

      if (
        (!school_id || `${studentSchoolId}` === `${school_id}`) &&
        (!teacher_id || studentTeacherId === teacher_id) &&
        (!course_ids || course_ids.includes(studentCourseId ?? -1)) &&
        (archived === undefined ||
          (archived === true && endDateInPast) ||
          (archived === false && !endDateInPast))
      ) {
        filteredStudents.push(student);
      }
    }
  });

  return filteredStudents;
};

interface IProgressReqMap {
  students: Partial<DbUser>[];
  moduleIds: string[];
}

export const getStudentsProgressData = async (
  studentMap: StudentMap,
  courseList: TeacherClassDetailsCourse[]
): Promise<Map<string, SummaryData>> => {
  try {
    // Create a mapping of course ID to array of class IDs and module IDs
    const courseStudentMap = new Map<number, IProgressReqMap>();
    Array.from(studentMap.values()).forEach((student) => {
      const studentCourseId = student.observer_details?.class?.course_id ?? -1;

      const matchedCourse = courseList.find(
        (course) => course.course_id === studentCourseId
      );
      if (matchedCourse) {
        if (courseStudentMap.has(studentCourseId)) {
          const studentModuleData = courseStudentMap.get(studentCourseId);
          studentModuleData?.students.push({ id: student.id, email: student.email });
        } else {
          courseStudentMap.set(studentCourseId, {
            students: [{ id: student.id, email: student.email }],
            moduleIds: matchedCourse.course_modules,
          });
        }
      }
    });

    // Initialize a map to store the student progress data
    const studentProgressDataMap = new Map<string, SummaryData>();

    // Loop through each course and send a separate API call
    const requests = Array.from(courseStudentMap.entries()).map(
      async ([courseId, classModuleData]) => {
        let response = await axios.post(`/insights/student-summary`, {
          type: "card",
          students: classModuleData.students,
          courseFilter: {
            moduleIds: classModuleData.moduleIds,
          },
        });

        // Update classProgressDataMap directly inside this loop
        response.data?.forEach((item: any) => {
          studentProgressDataMap.set(item.id, item as SummaryData);
        });
      }
    );

    // Wait for all requests to complete
    await Promise.all(requests);

    return studentProgressDataMap;
  } catch (err) {
    console.log(err);
    throw err;
  }
};
